import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";

export default function SecurityTab() {
  return (
    <div className="space-y-6">
      {/* Security & Privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security & Privacy
          </CardTitle>
          <CardDescription>How we protect your team's documentation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Authentication & Authorization</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">JWT Authentication:</strong> All API requests
                use secure JSON Web Tokens
              </li>
              <li>
                <strong className="text-foreground">Zero Trust Architecture:</strong> User identity
                derived from JWT only (never from client)
              </li>
              <li>
                <strong className="text-foreground">Row-Level Security:</strong> PostgreSQL RLS
                ensures you only see your team's data
              </li>
              <li>
                <strong className="text-foreground">Ownership Verification:</strong> All document
                operations verify ownership before execution
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Data Protection</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">SHA-256 Hashing:</strong> Documents identified
                by collision-resistant 64-character hashes
              </li>
              <li>
                <strong className="text-foreground">URL Normalization:</strong> Prevents duplicate
                imports via case-insensitive URL matching
              </li>
              <li>
                <strong className="text-foreground">Encrypted Connections:</strong> All traffic uses
                HTTPS/TLS encryption
              </li>
              <li>
                <strong className="text-foreground">PII Protection:</strong> Email addresses and
                sensitive data sanitized in production logs
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Security Score</h4>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
              <p className="text-sm">
                <strong>✅ 100% Security Score</strong> - All 9 critical vulnerabilities eliminated
                through comprehensive security hardening (JWT auth, SHA-256 hashing, zero hard-coded
                credentials, PII sanitization)
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm">
              <strong>🔒 Privacy Note:</strong> Your documentation content is stored securely in
              Supabase PostgreSQL. Only authenticated team members can access your documents. We
              never share your data with third parties.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Security Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Best Practices
          </CardTitle>
          <CardDescription>Recommendations for secure usage</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Team Member Management</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">Remove Inactive Users:</strong> Regularly review
                and remove team members who no longer need access
              </li>
              <li>
                <strong className="text-foreground">Immediate Revocation:</strong> When removing a
                user, their access tokens are immediately invalidated
              </li>
              <li>
                <strong className="text-foreground">User Status Checks:</strong> Every request
                verifies user is still active and part of the team
              </li>
              <li>
                <strong className="text-foreground">Email Domain Validation:</strong> Signup form
                restricts new accounts to @projectassistant.org and @projectassistant.io domains
                (frontend validation for email/password signups)
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">CLI Authentication</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">30-Day Token Expiry:</strong> Access tokens
                automatically expire after 30 days
              </li>
              <li>
                <strong className="text-foreground">SHA-256 Token Hashing:</strong> Tokens stored as
                hashes in database for additional security
              </li>
              <li>
                <strong className="text-foreground">Re-authenticate Regularly:</strong> Run{" "}
                <code className="bg-muted px-1 rounded text-xs">
                  npx @projectassistant/localdocs login
                </code>{" "}
                monthly to refresh
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Documentation Content</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>
                <strong className="text-foreground">Public Documentation Only:</strong> Only add
                documentation from public official sources
              </li>
              <li>
                <strong className="text-foreground">No Sensitive Data:</strong> Never add documents
                containing API keys, passwords, or private information
              </li>
              <li>
                <strong className="text-foreground">Review Before Export:</strong> Check exported
                documentation sets don't contain sensitive content
              </li>
            </ul>
          </div>

          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
            <p className="text-sm">
              <strong>⚠️ Important:</strong> While LocalDocs Hosted is secure, always follow your
              organization's security policies when adding and sharing documentation. Never upload
              proprietary or confidential information.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
